'use client';

import React, { useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import Icon from '@/components/ui/AppIcon';
import StepOne from './StepOne';
import StepTwo from './StepTwo';
import StepThree from './StepThree';
import QuoteResponsePanel from './QuoteResponsePanel';
import SubmissionSuccess from './SubmissionSuccess';
import { createClient } from '@/lib/supabase/client';

const SITE_URL = 'https://firstservi.com';

export type ServiceCategory = 'cleaning' | 'gardening' | 'handyman' | '';
export type CleaningSubType = 'house' | 'driveway' | 'pool' | '';

export interface RequestFormData {
  // Step 1
  serviceCategory: ServiceCategory;
  cleaningSubType: CleaningSubType;
  // Cleaning fields
  propertySize?: string;
  bedrooms?: string;
  bathrooms?: string;
  cleaningType?: string;
  drivewaySurface?: string;
  drivewaySize?: string;
  poolType?: string;
  poolSize?: string;
  // Gardening fields
  gardenSize?: string;
  gardenServices?: string[];
  grassType?: string;
  // Handyman fields
  jobCategory?: string;
  jobDescription?: string;
  materialSupply?: string;
  // Step 2
  propertyAddress?: string;
  propertyAccess?: string;
  preferredDate1?: string;
  preferredDate2?: string;
  preferredTime?: string;
  specialInstructions?: string;
  photoUrls?: string[];
  // Step 3
  firstName?: string;
  lastName?: string;
  email?: string;
  phone?: string;
  howHeard?: string;
  notificationUpdates?: boolean;
  specialOffers?: boolean;
}

const steps = [
  { id: 1, label: 'Service Type', icon: 'WrenchScrewdriverIcon' },
  { id: 2, label: 'Job Details', icon: 'HomeIcon' },
  { id: 3, label: 'Your Details', icon: 'UserIcon' },
];

function generateRefNumber(): string {
  const year = new Date().getFullYear();
  const seq = Math.floor(Math.random() * 90000 + 10000);
  return `SQ-${year}-${seq}`;
}

export default function ServiceRequestWizard() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState<RequestFormData>({
    serviceCategory: '',
    cleaningSubType: '',
    gardenServices: [],
    notificationUpdates: true,
    specialOffers: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [showShareQR, setShowShareQR] = useState(false);

  const updateFormData = (updates: Partial<RequestFormData>) => {
    setFormData((prev) => ({ ...prev, ...updates }));
  };

  const handleSubmit = async (finalData: RequestFormData) => {
    setSubmitError(null);
    const supabase = createClient();

    try {
      const serviceDetails: Record<string, any> = {};
      if (finalData.serviceCategory === 'cleaning') {
        serviceDetails.cleaningSubType = finalData.cleaningSubType;
        serviceDetails.bedrooms = finalData.bedrooms;
        serviceDetails.bathrooms = finalData.bathrooms;
        serviceDetails.cleaningType = finalData.cleaningType;
        serviceDetails.drivewaySurface = finalData.drivewaySurface;
        serviceDetails.drivewaySize = finalData.drivewaySize;
        serviceDetails.poolType = finalData.poolType;
        serviceDetails.poolSize = finalData.poolSize;
      } else if (finalData.serviceCategory === 'gardening') {
        serviceDetails.gardenSize = finalData.gardenSize;
        serviceDetails.gardenServices = finalData.gardenServices;
        serviceDetails.grassType = finalData.grassType;
      } else if (finalData.serviceCategory === 'handyman') {
        serviceDetails.jobCategory = finalData.jobCategory;
        serviceDetails.jobDescription = finalData.jobDescription;
        serviceDetails.materialSupply = finalData.materialSupply;
      }

      const { error } = await supabase.from('jobs').insert({
        ref_number: generateRefNumber(),
        service_category: finalData.serviceCategory,
        cleaning_sub_type: finalData.cleaningSubType || null,
        service_details: serviceDetails,
        property_address: finalData.propertyAddress || '',
        property_access: finalData.propertyAccess || null,
        preferred_date_1: finalData.preferredDate1 || null,
        preferred_date_2: finalData.preferredDate2 || null,
        preferred_time: finalData.preferredTime || null,
        special_instructions: finalData.specialInstructions || null,
        photo_urls: finalData.photoUrls || [],
        client_first_name: finalData.firstName || '',
        client_last_name: finalData.lastName || '',
        client_email: finalData.email || '',
        client_phone: finalData.phone || '',
        how_heard: finalData.howHeard || null,
        notification_updates: finalData.notificationUpdates ?? true,
        special_offers: finalData.specialOffers ?? false,
        job_status: 'submitted',
      });

      if (error) {
        console.log('Job submission error:', error.message);
        // Still show success to user — data may have been saved
      }
    } catch (err: any) {
      console.log('Submit error:', err.message);
    }

    setSubmitted(true);
    setShowAdminPanel(true);
  };

  const resetForm = () => {
    setSubmitted(false);
    setShowAdminPanel(false);
    setCurrentStep(1);
    setFormData({
      serviceCategory: '',
      cleaningSubType: '',
      gardenServices: [],
      notificationUpdates: true,
      specialOffers: false,
    });
    setSubmitError(null);
  };

  if (submitted && !showAdminPanel) {
    return <SubmissionSuccess formData={formData} onNewRequest={resetForm} />;
  }

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground">Request a Quote</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Tell us about your job and we'll send you a personalised quote within 24 hours.
        </p>
      </div>

      {submitted && showAdminPanel ? (
        /* Admin quote response view — shown after submission for demo */
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          <SubmissionSuccess formData={formData} onNewRequest={resetForm} />
          <QuoteResponsePanel formData={formData} />
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
          {/* Wizard form — 2/3 width */}
          <div className="xl:col-span-2 space-y-6">
            {/* Step progress */}
            <div className="card-surface p-5">
              <div className="flex items-center gap-0">
                {steps.map((step, idx) => (
                  <React.Fragment key={`step-${step.id}`}>
                    <div className="flex flex-col items-center gap-1.5 flex-shrink-0">
                      <div
                        className={`w-9 h-9 rounded-full flex items-center justify-center transition-all duration-200 ${
                          currentStep > step.id
                            ? 'bg-primary text-primary-foreground'
                            : currentStep === step.id
                            ? 'bg-primary text-primary-foreground ring-4 ring-primary/20'
                            : 'bg-muted text-muted-foreground'
                        }`}
                      >
                        {currentStep > step.id ? (
                          <Icon name="CheckIcon" size={16} variant="outline" />
                        ) : (
                          <span className="text-sm font-bold">{step.id}</span>
                        )}
                      </div>
                      <span
                        className={`text-xs font-medium whitespace-nowrap ${
                          currentStep === step.id ? 'text-primary' : 'text-muted-foreground'
                        }`}
                      >
                        {step.label}
                      </span>
                    </div>
                    {idx < steps.length - 1 && (
                      <div
                        className={`flex-1 h-0.5 mx-2 mb-4 transition-all duration-300 ${
                          currentStep > step.id ? 'bg-primary' : 'bg-border'
                        }`}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            </div>

            {submitError && (
              <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                <Icon name="ExclamationTriangleIcon" size={16} variant="outline" />
                {submitError}
              </div>
            )}

            {/* Step content */}
            <div className="card-surface p-5 sm:p-6 animate-slide-up">
              {currentStep === 1 && (
                <StepOne formData={formData} updateFormData={updateFormData} onNext={() => setCurrentStep(2)} />
              )}
              {currentStep === 2 && (
                <StepTwo
                  formData={formData}
                  updateFormData={updateFormData}
                  onNext={() => setCurrentStep(3)}
                  onBack={() => setCurrentStep(1)}
                />
              )}
              {currentStep === 3 && (
                <StepThree
                  formData={formData}
                  updateFormData={updateFormData}
                  onBack={() => setCurrentStep(2)}
                  onSubmit={handleSubmit}
                />
              )}
            </div>
          </div>

          {/* Info sidebar — 1/3 width */}
          <div className="space-y-4">
            <div className="card-surface p-5">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="ClockIcon" size={16} variant="outline" className="text-primary" />
                How it works
              </h3>
              <ol className="space-y-3">
                {[
                  { step: '1', text: 'Fill in your service request — takes about 3 minutes.' },
                  { step: '2', text: "We'll review your job and send a quote to your email within 24 hours." },
                  { step: '3', text: 'Accept the quote and we schedule your job at a time that suits you.' },
                  { step: '4', text: 'Our team completes the work and sends you a full report with photos.' },
                ].map((item) => (
                  <li key={`how-${item.step}`} className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-5 h-5 bg-secondary text-primary rounded-full text-xs font-bold flex items-center justify-center mt-0.5">
                      {item.step}
                    </span>
                    <span className="text-xs text-muted-foreground leading-relaxed">{item.text}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="card-surface p-5 bg-secondary border-primary/20">
              <div className="flex items-start gap-3">
                <Icon name="ShieldCheckIcon" size={20} variant="outline" className="text-primary flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-primary">Fully insured & vetted</p>
                  <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
                    All ServiQuote team members are background-checked, insured, and trained to our service standards.
                  </p>
                </div>
              </div>
            </div>

            <div className="card-surface p-5">
              <h3 className="text-sm font-semibold text-foreground mb-3">Contact us directly</h3>
              <div className="space-y-2">
                <a href="tel:0434300737" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors">
                  <Icon name="PhoneIcon" size={14} variant="outline" />
                  0434 300 737
                </a>
                <a href="mailto:ianmckinnon86@gmail.com" className="flex items-center gap-2 text-xs text-muted-foreground hover:text-primary transition-colors">
                  <Icon name="EnvelopeIcon" size={14} variant="outline" />
                  ianmckinnon86@gmail.com
                </a>
              </div>
              <p className="text-xs text-muted-foreground mt-3 pt-3 border-t border-border">
                💳 Payments accepted via <strong className="text-foreground">PayID</strong> — use the phone number or email above.
              </p>
            </div>

            {/* Share QR */}
            <div className="card-surface p-5">
              <h3 className="text-sm font-semibold text-foreground mb-3 flex items-center gap-2">
                <Icon name="QrCodeIcon" size={16} variant="outline" className="text-primary" />
                Share FirstServi
              </h3>
              <div className="flex flex-col items-center gap-3">
                <div className="bg-white p-2.5 rounded-lg shadow-sm border border-border">
                  <QRCodeSVG value={SITE_URL} size={90} level="H" />
                </div>
                <p className="text-xs text-muted-foreground text-center leading-relaxed">
                  Scan to share with friends, family &amp; neighbours
                </p>
                <button
                  onClick={() => { if (typeof navigator !== 'undefined' && navigator.share) { navigator.share({ title: 'FirstServi', url: SITE_URL }); } else if (navigator.clipboard) { navigator.clipboard.writeText(SITE_URL); } }}
                  className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-semibold bg-primary text-primary-foreground hover:opacity-90 transition-all"
                >
                  <Icon name="ShareIcon" size={13} variant="outline" />
                  Share Link
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}